import { useComponentsLoader } from '@hooks';
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import LoaderCart from '../common/LoaderCart';
import PDWebModal from '../common/PDWebModal';
import { modalsActions } from '../../redux/slices/modals';
import PDWebPortalWrapper from './PDWebPortalWrapper';
import { useAppHeight } from '../../hooks';

const RXC_EXIT_POPUP = lazy(
  () => import('../backoffice/RXC_EXIT_POPUP/RXC_EXIT_POPUP')
);
const RXC_HOW_TO_READ_YOUR_PRESCRIPTION_MODAL = lazy(
  () =>
    import(
      '../backoffice/RXC_HOW_TO_READ_YOUR_PRESCRIPTION_MODAL/RXC_HOW_TO_READ_YOUR_PRESCRIPTION_MODAL'
    )
);
const RXC_PDWEB_MODAL = lazy(
  () => import('../backoffice/RXC_PDWEB_MODAL/RXC_PDWEB_MODAL')
);
const RXC_PD_MODAL = lazy(
  () => import('../backoffice/RXC_PD_MODAL/RXC_PD_MODAL')
);
const RXC_PD_OPTY_MODAL = lazy(
  () => import('../backoffice/RXC_PD_OPTY_MODAL/RXC_PD_OPTY_MODAL')
);
const RXC_ADD_DOCTOR_MODAL = lazy(
  () => import('../backoffice/RXC_ADD_DOCTOR_MODAL/RXC_ADD_DOCTOR_MODAL')
);
const LearnMoreModal = lazy(() => import('../common/LearnMoreModal'));
const VirtualMirror = lazy(() => import('../common/VirtualMirror'));
const PdWeb = lazy(() => import('../common/PdWeb'));
const NoPowerModal = lazy(
  () => import('../backoffice/RXC_0_POWER_POPUP/RXC_0_POWER_POPUP')
);

function Layout({ data }) {
  const {
    exitModal,
    learnMoreFocus,
    showPrescriptionModalContinue,
    showDigitalOptometryModal,
    showPrescriptionModalPD,
    showAddMyDoctorModal,
    showPrescriptionModalHowToRead,
    showPdWebModal,
  } = useSelector((state: any) => state.modals);

  const showVirtualMirror = useSelector(
    (state: any) => state.virtualMirror?.showVirtualMirror
  );

  const showPdWeb = useSelector((state: any) => state.pdWeb?.showPdWeb);

  const leftComponents = useComponentsLoader(data.previewSection.components);

  const defaultComponents = useSelector(
    (state: any) => state.workflow.defaultComponents
  );

  const modalRef = useRef(null);

  const dispatch = useDispatch();

  // ultimo elemento focussato prima dell'apertura di una modale
  const [lastFocusedElement, setLastFocusedElement] = useState(null);

  const setupFocusTrap = useCallback(
    (containerRef) => {
      if (!containerRef.current) return null;

      // 1. TROVA TUTTI GLI ELEMENTI FOCUSABILI
      const getFocusableElements = () => {
        const focusableSelectors = [
          'button:not([disabled]):not(.RXC_CUSTOM_SELECT_CELL)',
          '[href]:not([disabled])',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"]):not([disabled])',
        ].join(', ');

        const elements =
          containerRef.current.querySelectorAll(focusableSelectors);

        // Filtra elementi realmente visibili e focusabili
        let returnedElements = Array.from(elements).filter((element) => {
          if (trapFocusModal()) {
            return (
              element.offsetParent !== null && // Non nascosto con display:none
              getComputedStyle(element).visibility !== 'hidden' && // Non nascosto con visibility
              getComputedStyle(element).opacity !== '0' && // Non trasparente
              !element.hasAttribute('inert') && // Non inert
              element.getBoundingClientRect().width > 0 && // Ha dimensioni
              element.getBoundingClientRect().height > 0 &&
              (element.closest('.RXC_MODAL') ||
                element.closest('.PDWebModal_overlay'))
            );
          } else {
            return (
              element.offsetParent !== null && // Non nascosto con display:none
              getComputedStyle(element).visibility !== 'hidden' && // Non nascosto con visibility
              getComputedStyle(element).opacity !== '0' && // Non trasparente
              !element.hasAttribute('inert') && // Non inert
              element.getBoundingClientRect().width > 0 && // Ha dimensioni
              element.getBoundingClientRect().height > 0
            );
          }
        });

        if (showPrescriptionModalHowToRead?.showModal) {
          returnedElements.filter((el) => {
            if (el.nodeName === 'INPUT') {
              return !!el.closest('.RXC_TABLE_ROW_HIGHLIGHT');
            } else {
              return true;
            }
          });
        }

        return returnedElements;
      };

      // 1.1 VERIFICA SE C'È UNA MODAL APERTA
      const trapFocusModal = () => {
        return [
          exitModal,
          learnMoreFocus,
          showPrescriptionModalContinue,
          showDigitalOptometryModal,
          showPrescriptionModalPD,
          showAddMyDoctorModal,
          showPrescriptionModalHowToRead?.showModal,
          showPdWebModal,
          showPdWeb,
        ].some((el) => !!el);
      };

      // 2. GESTIONE DELLA NAVIGAZIONE
      const handleTabKey = (event) => {
        const focusableElements = getFocusableElements();

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentElement = document.activeElement;

        // Se Tab normale (avanti)
        if (event.key === 'Tab' && !event.shiftKey) {
          // Se siamo sull'ultimo elemento, vai al primo
          if (currentElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
            return;
          }

          // Se il focus è fuori dal container, portalo al primo elemento
          if (
            !containerRef.current.contains(currentElement) ||
            !focusableElements.includes(currentElement)
          ) {
            event.preventDefault();
            firstElement.focus();
            return;
          }

          // Se il focus è nel container, portalo all'elemento successivo della modale
          if (focusableElements.includes(currentElement) && trapFocusModal()) {
            event.preventDefault();
            const currentIndex = focusableElements.indexOf(currentElement);
            focusableElements[currentIndex + 1].focus();
            return;
          }
        }

        // Se Shift+Tab (indietro)
        if (event.key === 'Tab' && event.shiftKey) {
          // Se siamo sul primo elemento, vai all'ultimo
          if (currentElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
            return;
          }

          // Se il focus è fuori dal container, portalo all'ultimo elemento
          if (
            !containerRef.current.contains(currentElement) ||
            !focusableElements.includes(currentElement)
          ) {
            event.preventDefault();
            lastElement.focus();
            return;
          }

          // Se il focus è nel container, portalo all'elemento precedente della modale
          if (focusableElements.includes(currentElement) && trapFocusModal()) {
            event.preventDefault();
            const currentIndex = focusableElements.indexOf(currentElement);
            focusableElements[currentIndex - 1].focus();
            return;
          }
        }
      };

      // 3. Gestione modali
      const handleModals = (event) => {
        if (event.key === 'Escape') {
          if (learnMoreFocus) {
            dispatch(modalsActions.setOpenLearnMoreModal(''));
          } else if (showPrescriptionModalContinue) {
            dispatch(modalsActions.setShowPrescriptionModalContinue(false));
          } else if (showDigitalOptometryModal) {
            dispatch(modalsActions.setShowDigitalOptometryModal(false));
          } else if (showPrescriptionModalPD) {
            dispatch(modalsActions.setShowPrescriptionModalPD(false));
          } else if (showAddMyDoctorModal) {
            dispatch(modalsActions.setShowAddMyDoctorModal(false));
          } else if (showPrescriptionModalHowToRead?.showModal) {
            dispatch(
              modalsActions.setShowPrescriptionModalHowToRead({
                showModal: false,
              })
            );
          } else if (showPdWebModal) {
            dispatch(modalsActions.setShowPdWebModal(false));
          } else {
            dispatch(modalsActions.toggleExitModal(!exitModal));
          }
        }
      };

      // 4. EVENTO HANDLER PRINCIPALE
      const keydownHandler = (event) => {
        handleTabKey(event);
        handleModals(event);
      };

      // 5. SETUP INIZIALE
      const initialize = () => {
        const focusableElements = getFocusableElements();

        // Focus sul primo elemento focusabile
        if (focusableElements.length > 0) {
          // Piccolo delay per assicurarsi che il DOM sia ready
          requestAnimationFrame(() => {
            focusableElements[0].focus();
          });
        }

        // Aggiungi event listener
        document.addEventListener('keydown', keydownHandler, true);

        // Imposta aria-hidden sul resto della pagina (opzionale ma raccomandato)
        // const otherElements = document.querySelectorAll(
        //   'body > *:not([data-focus-trap])'
        // );
        // otherElements.forEach((el) => {
        //   if (el !== containerRef.current) {
        //     el.setAttribute('aria-hidden', 'true');
        //   }
        // });
      };

      // 6. CLEANUP
      const cleanup = () => {
        document.removeEventListener('keydown', keydownHandler, true);

        // Rimuovi aria-hidden
        // const hiddenElements = document.querySelectorAll('[aria-hidden="true"]');
        // hiddenElements.forEach((el) => {
        //   el.removeAttribute('aria-hidden');
        // });
      };

      // 7. RETURN DELLE FUNZIONI UTILI
      return {
        initialize,
        cleanup,
        getFocusableElements,
        refreshTrap: () => {
          // Utile se il contenuto del modal cambia dinamicamente
          const elements = getFocusableElements();
          if (
            elements.length > 0 &&
            (!containerRef.current.contains(document.activeElement) ||
              trapFocusModal())
          ) {
            elements[0].focus();
          }
        },
      };
    },
    [
      exitModal,
      learnMoreFocus,
      showPrescriptionModalContinue,
      showDigitalOptometryModal,
      showPrescriptionModalPD,
      showAddMyDoctorModal,
      showPrescriptionModalHowToRead?.showModal,
      showPdWebModal,
      showPdWeb,
    ]
  );

  // USO NEL useEffect:

  useEffect(() => {
    if (!modalRef.current) return;
    const focusTrap = setupFocusTrap(modalRef);
    if (focusTrap) {
      focusTrap.initialize();

      // Se il DOM cambia, aggiorna il trap
      const observer = new MutationObserver((mutations) => {
        const shouldRefresh = mutations.some((mutation) => {
          if (mutation.type === 'attributes') {
            return ['disabled', 'tabindex', 'hidden', 'inert'].includes(
              mutation.attributeName || ''
            );
          }
          return mutation.type === 'childList';
        });
        if (shouldRefresh) {
          focusTrap.refreshTrap();
        }
      });

      observer.observe(modalRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'tabindex', 'hidden', 'inert'],
      });

      if (
        [
          exitModal,
          learnMoreFocus,
          showPrescriptionModalContinue,
          showDigitalOptometryModal,
          showPrescriptionModalPD,
          showAddMyDoctorModal,
          showPrescriptionModalHowToRead?.showModal,
          showPdWebModal,
          showPdWeb,
        ].some((showModal) => showModal)
      ) {
        setLastFocusedElement(document.activeElement);
      } else {
        if (lastFocusedElement) {
          requestAnimationFrame(() => {
            if (
              !focusTrap.getFocusableElements().includes(lastFocusedElement)
            ) {
              lastFocusedElement
                .closest(
                  'button:not([disabled]):not(.RXC_CUSTOM_SELECT_CELL)',
                  '[href]:not([disabled])',
                  'input:not([disabled])',
                  'select:not([disabled])',
                  'textarea:not([disabled])',
                  '[tabindex]:not([tabindex="-1"]):not([disabled])'
                )
                .focus();
            } else {
              lastFocusedElement.focus();
            }
          });
          setLastFocusedElement(null);
        }
      }
      return () => {
        focusTrap.cleanup();
        observer.disconnect();
      };
    }
  }, [
    window.rxcBrain.currentStep,
    exitModal,
    learnMoreFocus,
    showPrescriptionModalContinue,
    showDigitalOptometryModal,
    showPrescriptionModalPD,
    showAddMyDoctorModal,
    showPrescriptionModalHowToRead?.showModal,
    showPdWebModal,
    showPdWeb,
  ]);
  const appHeight = useAppHeight();
  return (
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      style={{ minHeight: '100lvh' }}
      className={`RXC RXC_LAYOUT_WRAPPER tw-z-50 tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-flex tw-overflow-hidden ${data.definingAttributes.channel}`}
    >
      {/* MODALS */}
      <div id="headlessui-portal-root">
        <div id="headlessui-div-portal"></div>
      </div>
      {showPrescriptionModalContinue && (
        <Suspense fallback={null}>
          <NoPowerModal {...defaultComponents.RXC_0_POWER_POPUP} />
        </Suspense>
      )}

      {exitModal && (
        <Suspense fallback={null}>
          <RXC_EXIT_POPUP
            {...defaultComponents.RXC_EXIT_POPUP}
            className="RXC_EXIT_POPUP_MODAL"
          />
        </Suspense>
      )}
      {showPrescriptionModalHowToRead?.showModal && (
        <Suspense fallback={null}>
          <RXC_HOW_TO_READ_YOUR_PRESCRIPTION_MODAL
            {...defaultComponents.RXC_HOW_TO_READ_YOUR_PRESCRIPTION_MODAL}
            showNone={showPrescriptionModalHowToRead?.showNone}
          />
        </Suspense>
      )}

      {showPrescriptionModalPD && (
        <Suspense fallback={null}>
          <RXC_PD_MODAL {...defaultComponents.RXC_PD_MODAL} />
        </Suspense>
      )}
      {showPdWebModal && (
        <Suspense fallback={null}>
          <RXC_PDWEB_MODAL {...defaultComponents.RXC_PDWEB_MODAL} />
        </Suspense>
      )}
      {showDigitalOptometryModal && (
        <Suspense fallback={null}>
          <RXC_PD_OPTY_MODAL {...defaultComponents.RXC_PD_OPTY_MODAL} />
        </Suspense>
      )}
      {showAddMyDoctorModal && (
        <Suspense fallback={null}>
          <RXC_ADD_DOCTOR_MODAL {...defaultComponents.RXC_ADD_DOCTOR_MODAL} />
        </Suspense>
      )}
      {showVirtualMirror && (
        <Suspense fallback={null}>
          <VirtualMirror />
        </Suspense>
      )}
      {showPdWeb && (
        <PDWebPortalWrapper>
          <Suspense fallback={null}>
            <PdWeb />
          </Suspense>
        </PDWebPortalWrapper>
      )}

      {learnMoreFocus && (
        <Suspense fallback={null}>
          <LearnMoreModal />
        </Suspense>
      )}

      {/* PREVIEW SECTION */}

      <div className="RXC_PREVIEW_SECTION tw-relative tw-flex-1 tw-w-1/2 tw-max-w-1/2 tw-h-screen tw-hidden lg:tw-flex lg:tw-items-center lg:tw-justify-center tw-bg-BaseGrayUltraLight">
        {leftComponents}
      </div>

      {/* STEP SECTION */}

      <div className="RXC_STEP_SECTION tw-main-grid tw-relative tw-flex-1 tw-w-1/2 tw-h-100">
        {window.rxcBrain.packageFromCart && <LoaderCart />}
        <Outlet />
      </div>
    </div>
  );
}

export default Layout;
